import * as React from 'react';
import styled from 'styled-components';
import cl from 'classnames';
import { get } from 'lodash';
import { LazyImage } from '../../../components/LazyImage';
import { Typography } from '../../../components/DesignSystemV2/Typography';
import { colorsV2 } from '../../../style/colors-v2';
import { Swiper, SwiperSlide } from 'swiper/react/swiper-react';
import { ChevronLeft, ChevronRight } from '@dolstaff/shared/es/icons';
import { FreeMode, Navigation } from 'swiper';
import Button from 'components/Button';
import { Link } from 'gatsby';

const NAVIGATION_BUTTON_SIZE = 40;

const CardInfo = styled.div``;
const TeacherCard = styled.div`
  cursor: pointer;
  border-radius: 8px;
  border: 1px solid ${colorsV2.gray40};
  width: 140px;
  height: 100%;
  padding: 12px;

  display: grid;
  place-items: center;
  row-gap: 4px;
  text-align: center;
  img {
    width: 64px;
    height: 64px;
    opacity: 0.7;
  }

  :hover {
    border: 1px solid ${colorsV2.primary100};
    ${CardInfo} {
      .teacher-name {
        color: ${colorsV2.primary100};
      }
    }
  }

  &.active {
    background-color: ${colorsV2.primary10};
    border: 1px solid ${colorsV2.primary100};
    ${CardInfo} {
      .teacher-name {
        color: ${colorsV2.primary100};
      }
    }
    img {
      opacity: 1;
    }
  }
`;
const TeacherCarouselWrapper = styled.div`
  margin-top: 40px;
  position: relative;

  .swiper {
    position: relative;
    &:after {
      position: absolute;
      z-index: 4;
      content: '';
      right: -1px;
      top: 0;
      height: 100%;
      width: 113px;
      background: linear-gradient(
        270deg,
        #ffffff 0%,
        rgba(255, 255, 255, 0) 100%
      );
    }
    &.end-slider {
      &:after {
        display: none;
      }
    }
  }

  .swiper-slide {
    width: fit-content !important;
    height: auto;
  }

  .navigation-action {
    display: none;
    color: ${colorsV2.black100};
    cursor: pointer;
    position: absolute;
    min-width: ${NAVIGATION_BUTTON_SIZE}px;
    min-height: ${NAVIGATION_BUTTON_SIZE}px;
    max-width: ${NAVIGATION_BUTTON_SIZE}px;
    max-height: ${NAVIGATION_BUTTON_SIZE}px;
    filter: drop-shadow(0px 2px 5px rgba(0, 0, 0, 0.32));
    place-items: center;
    border-radius: 50%;
    background-color: ${colorsV2.white100};
    font-size: 28px;
    line-height: 0;
    z-index: 2;

    display: grid;
    top: 30%;

    &.next {
      right: -${NAVIGATION_BUTTON_SIZE / 2}px;
    }
    &.prev {
      left: -${NAVIGATION_BUTTON_SIZE / 2}px;
    }
    &.disabled {
      display: none;
    }
  }
`;

const TeacherCarousel = ({ activeIndex, data, setActiveIndex }) => {
  const [reachEndSlider, setReachEndSlider] = React.useState(false);

  return (
    <TeacherCarouselWrapper>
      <>
        <div className={cl('navigation-action', `${prefix}`, 'next')}>
          <ChevronRight />
        </div>
        <div className={cl('navigation-action', `${prefix}`, 'prev')}>
          <ChevronLeft />
        </div>
      </>
      <Swiper
        pagination={{
          clickable: true
        }}
        navigation={{
          nextEl: `${prefix ? `.${prefix}` : ''}.next`,
          prevEl: `${prefix ? `.${prefix}` : ''}.prev`,
          disabledClass: 'disabled'
        }}
        modules={[Navigation, FreeMode]}
        spaceBetween={10}
        freeMode={{
          enabled: true,
          minimumVelocity: 0.05,
          momentum: true
        }}
        className={cl({ 'end-slider': reachEndSlider })}
        slidesPerView="auto"
        slidesPerGroup={2}
        onReachEnd={() => {
          setReachEndSlider(true);
        }}
      >
        {data.map((item, index) => (
          <SwiperSlide key={item.name + item.description}>
            <TeacherCard
              className={cl({
                active: activeIndex === index
              })}
              onClick={() => {
                setActiveIndex(index);
              }}
              id={get(item, 'url', '').replace(/#/gi, '').toLowerCase()}
              key={item.id}
            >
              <LazyImage handle={item.detailImageHandler} alt={item.fullName} />
              <CardInfo>
                <Typography
                  desktopVariant="medium/20-28"
                  variant="medium/16-24"
                  className="teacher-name"
                  v3
                  color={colorsV2.black80}
                >
                  {item.fullName}
                </Typography>
              </CardInfo>
            </TeacherCard>
          </SwiperSlide>
        ))}
      </Swiper>
    </TeacherCarouselWrapper>
  );
};

const TeacherAvatar = styled.div`
  position: relative;
  height: fit-content;
  padding-top: 63.25%;
  .teacher-img {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    img {
      border-radius: 8px;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .linear-badge {
    width: fit-content;
    position: absolute;
    left: 0;
    right: 0;
    bottom: 8px;
    background: ${colorsV2.white100};
    padding: 4px 8px;
    border-radius: 20px;
    display: flex;
    align-items: center;
    margin: 0 auto;
  }
`;
const HighlightItem = styled.div`
  display: grid;
  grid-template-columns: 24px auto;
  grid-column-gap: 12px;
  align-items: center;
  min-height: 44px;
`;
const HighlightItemList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-content: center;
  grid-auto-rows: min-content;
  grid-row-gap: 8px;
  margin-top: 20px;
  ${HighlightItem} {
    grid-template-columns: 28px auto;
  }
`;
const TeacherHighlight = styled.div`
  display: grid;
  grid-template-columns: 276px auto;
  gap: 12px;
`;
const TeacherNameSection = styled.div`
  display: grid;
  gap: 4px;
`;
const TeacherInfoWrapper = styled.div`
  margin-top: 32px;
`;

const ButtonWrapper = styled.div`
  margin-top: 32px;
  text-align: center;
  button {
    border-radius: 8px;
    width: 344px;
  }
`;

const TeacherInfo = ({ activeIndex, data }) => {
  const selectedTeacher = data[activeIndex];
  return (
    <TeacherInfoWrapper>
      <TeacherHighlight>
        <TeacherAvatar>
          <LazyImage
            handle={selectedTeacher.listingImageHandler}
            className="teacher-img"
            alt={selectedTeacher.fullName}
          />
          <div className="linear-badge">
            <LazyImage
              handle="ww4nKqwCRp6UIHCk2DOe"
              alt="icon-linearthinking"
            />
            <Typography variant="medium/12-20" color={colorsV2.primary100}>
              Linearthinking Ambassador
            </Typography>
          </div>
        </TeacherAvatar>
        <div>
          <TeacherNameSection>
            <Typography variant="semi-bold/20-28">
              {get(selectedTeacher, 'fullName', '...')}
            </Typography>
            <Typography color={colorsV2.black80} variant="medium/14-20">
              {get(selectedTeacher, 'displayNameSubtitle', '...')}
            </Typography>
          </TeacherNameSection>
          <HighlightItemList>
            {get(selectedTeacher, 'highlights', []).map(highlight => {
              const { id, title, subText, iconHandler } = highlight;
              if (id !== 'linear-badge')
                return (
                  <HighlightItem key={id}>
                    <div className="icon">
                      <LazyImage handle={iconHandler} alt="icon-highlight" />
                    </div>
                    <div className="highlight-content">
                      <div className="title">
                        <Typography variant="semi-bold/16-24">
                          {title}
                        </Typography>
                      </div>
                      <div className="sub-text">
                        <Typography
                          variant="regular/14-20"
                          color={colorsV2.black80}
                        >
                          {subText}
                        </Typography>
                      </div>
                    </div>
                  </HighlightItem>
                );
            })}
          </HighlightItemList>
        </div>
      </TeacherHighlight>
      <ButtonWrapper>
        <Link to={selectedTeacher.slug}>
          <Button outline>
            <Typography
              style={{ color: colorsV2.primary100 }}
              variant="semi-bold/14-24"
            >
              Xem thông tin giáo viên
            </Typography>
          </Button>
        </Link>
      </ButtonWrapper>
    </TeacherInfoWrapper>
  );
};
const ContentSection = styled.div``;

const prefix = 'teacher-card';

const TeachersHorizontalCarouselWrapper = styled.div``;
export const TeacherTablet = props => {
  return (
    <TeachersHorizontalCarouselWrapper>
      <ContentSection>
        <Typography variant="bold/32-40" as="h2">
          Đội ngũ giáo viên
        </Typography>
        <TeacherCarousel {...props} />
        <TeacherInfo {...props} />
      </ContentSection>
    </TeachersHorizontalCarouselWrapper>
  );
};
